import moment from 'moment'
import { JobRepository } from '../domain/job.repository'
import { ClaimActions } from '../domain/items'
import { ItemRepository } from '../domain/item.repository'
import { getSlotsAvailabilityRescheduleV1 } from '../../../../domain/flag-management/flag-management.services'
import { SlotV1 } from '../domain/job'
import { standardDate } from '../../../../utils/dates'
import { FULL_DATE_TIME_FORMAT, getFormattedDate } from '../../../utils/dates'

const MINUTE_IN_MILLISECONDS = 60000

export const slotsUseCase = (jobRepository: JobRepository, itemRepository: ItemRepository) => ({
  getAvailableSlots: async ({
    job: { jobId, storeTzSlotTo, storeTzSlotFrom },
    operationModel,
    allowWithoutCapacity = false,
    items,
    dayRange = 6
  }: GetAvailableSlotsType) => {
    const slotTo = new Date(
      getFormattedDate({
        date: standardDate(storeTzSlotTo),
        format: FULL_DATE_TIME_FORMAT
      })
    )
    const slotFrom = new Date(
      getFormattedDate({
        date: standardDate(storeTzSlotFrom),
        format: FULL_DATE_TIME_FORMAT
      })
    )
    const slotSizeMinutes = getSlotSize(slotFrom, slotTo)
    const timeWindow = {
      from: moment().toISOString(),
      to: moment()
        .add(dayRange - 1, 'days')
        .toISOString()
    }

    if (allowWithoutCapacity) {
      const slotsV1 = (await getSlotsAvailabilityRescheduleV1(jobId, {
        start: timeWindow.from,
        end: timeWindow.to
      })) as GetSlotsAvailabilityRescheduleV1Type

      return sanitazeSlotsResponseV1(slotsV1)
    }

    const [job, jobItem] = await Promise.all([jobRepository.getJobById(jobId), itemRepository.getItemsByJob(jobId)])
    const { logisticCostCurrency, origin, destination, operationModel: originalOperationModel, storeReference } = job

    const _items = items.map((i) => {
      const item = jobItem.find((item) => item.itemId === i.id)
      return {
        id: item.itemId,
        name: item.name,
        photoUrl: item.photoUrl,
        unit: item.presentationV2.unit,
        subUnit: item.presentationV2.subUnit,
        quantity: i.quantity,
        subQuantity: i.quantity,
        price: item.presentationV2.price,
        weight: item.presentationV2.weight,
        volume: item.presentationV2.volume,
        barcodes: item?.barcodes,
        claimInformation: !!item?.claimInformation?.availableClaimActions
          ? {
              availableClaimActions: item.claimInformation?.availableClaimActions,
              maxTimeToClaimInHours: moment(item.claimInformation?.maxClaimDate).diff(item.createdAt, 'hours')
            }
          : undefined,
        attributes: {
          ean: item.attributes.ean,
          category: item.attributes.category,
          sku: item?.attributes?.sku,
          equivalence: item?.attributes?.equivalence,
          location: item?.attributes?.location,
          originalPrice: item?.attributes?.originalPrice,
          plu: item?.attributes?.plu,
          posPrice: item?.attributes?.posPrice
        },
        comment: item.comment
      }
    })

    const data = {
      start: timeWindow.from,
      end: timeWindow.to,
      minimunSlotSize: slotSizeMinutes,
      slotSize: slotSizeMinutes,
      currencyCode: logisticCostCurrency,
      operationalModelsPriority: operationModel ? operationModel : originalOperationModel,
      origin,
      destination: {
        zipCode: destination?.zipCode,
        addressTwo: destination?.addressTwo,
        ...destination
      },
      jobItems: _items,
      express: false,
      fallback: false,
      storeReference
    }

    const slotsV2 = await jobRepository.getItemSlots(data)
    return slotsV2
  }
})

type GetAvailableSlotsType = {
  job: {
    jobId: string
    storeTzSlotTo: string
    storeTzSlotFrom: string
  }
  allowWithoutCapacity?: boolean
  operationModel?: string
  items: { id: string; selectedActions?: ClaimActions; quantity: number }[]
  dayRange?: number
}

type GetSlotsAvailabilityRescheduleV1Type = {
  slots: {
    express: {
      from: string
      to: string
    }[]
    statics: {
      from: string
      to: string
    }[]
  }
}

/**
 * This function infers the slot size based on the time range of the job
 * It expects the from and to dates from a previous job and calculates the slot size
 * based on the difference between the two dates and Nebula business rules
 * @param from
 * @param to
 * @returns slot size in minutes
 */
function getSlotSize(from: Date, to: Date): number {
  const rawSlotSizeMinutes = (to.getTime() - from.getTime()) / MINUTE_IN_MILLISECONDS + 1
  if (rawSlotSizeMinutes % 15 === 0) {
    return rawSlotSizeMinutes
  }
  const upperPossibleSlotMinutes = Math.ceil(rawSlotSizeMinutes / 15) * 15
  const lowerPossibleSlotMinutes = Math.floor(rawSlotSizeMinutes / 15) * 15
  return upperPossibleSlotMinutes - rawSlotSizeMinutes < rawSlotSizeMinutes - lowerPossibleSlotMinutes
    ? upperPossibleSlotMinutes
    : lowerPossibleSlotMinutes
}

function sanitazeSlotsResponseV1(slots: GetSlotsAvailabilityRescheduleV1Type) {
  return {
    express: slots.slots.express.map((slot) => ({
      from: slot.from,
      to: slot.to
    })) as SlotV1[],
    static: slots.slots.statics.map((slot) => ({
      from: slot.from,
      to: slot.to
    })) as SlotV1[]
  }
}
